// services/haptics.ts
//
// Single multi-platform haptic-feedback service.
//
// Supported targets:
//   - iOS native             → Capacitor @capacitor/haptics (the Taptic engine)
//   - Android native         → our own `NativeHaptics` plugin. That package's
//                              Android class is not a haptic engine: it runs the
//                              vibration MOTOR for 50ms at a fixed amplitude,
//                              which is the old-phone buzz rather than a tap.
//                              See android/…/NativeHapticsPlugin.java.
//   - Telegram Mini App      → window.Telegram.WebApp.HapticFeedback
//   - Web (Android browsers) → navigator.vibrate (Vibration API)
//   - Web (iOS Safari)       → <input type="checkbox" switch> toggle trick
//
// iOS Safari note: Safari never shipped the Vibration API. The only way to
// produce a haptic from the web is the non-standard `<input switch>` control
// (Safari 17.4+), which fires a system haptic when toggled. Calling .click()
// on its <label> triggers that programmatically on iOS 17.4–26.4.
//
// ⚠️ iOS 26.5 patched the programmatic path: label.click() no longer fires a
// haptic — only a *genuine* user tap on the switch does. So on 26.5+ the
// global impact()/notification() calls degrade to a silent no-op, and the only
// way to get a web haptic is `bindElement()`, which overlays a real switch the
// user physically taps. See https://github.com/tijnjh/ios-haptics for context.

import { Capacitor, registerPlugin } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import type { HapticFeedbackOptions } from '../types/platform';

export type HapticImpactStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
export type HapticNotificationType = 'success' | 'warning' | 'error';

type HapticTarget = 'telegram' | 'native' | 'web';

/** Capacitor's ImpactStyle only has Light/Medium/Heavy — map rigid/soft onto them. */
const CAPACITOR_IMPACT: Record<HapticImpactStyle, ImpactStyle> = {
    light: ImpactStyle.Light,
    soft: ImpactStyle.Light,
    medium: ImpactStyle.Medium,
    rigid: ImpactStyle.Heavy,
    heavy: ImpactStyle.Heavy,
};

const CAPACITOR_NOTIFICATION: Record<HapticNotificationType, NotificationType> = {
    success: NotificationType.Success,
    warning: NotificationType.Warning,
    error: NotificationType.Error,
};

/**
 * The Android replacement for @capacitor/haptics, which is right on iOS and a
 * buzz on Android — see the header. `registerPlugin` is a NAME LOOKUP, not an
 * import: the proxy exists everywhere and says nothing about whether the native
 * class is in this binary, so `isPluginAvailable` is the only thing that does.
 * That probe is load-bearing rather than defensive — a Capgo live update can put
 * this bundle inside an older binary that predates the plugin, and there the
 * honest answer is the package's buzz rather than silence.
 *
 * ImpactStyle and NotificationType are string enums whose values are exactly the
 * LIGHT/MEDIUM/HEAVY and SUCCESS/WARNING/ERROR this plugin expects, so the maps
 * above feed both paths and there is no second mapping to drift.
 */
type NativeHapticsPlugin = {
    impact(options: { style: ImpactStyle }): Promise<void>;
    notification(options: { type: NotificationType }): Promise<void>;
};

const NativeHaptics = registerPlugin<NativeHapticsPlugin>('NativeHaptics');

/** The custom plugin, or null on iOS / web / a binary that predates it. */
function osHaptics(): NativeHapticsPlugin | null {
    return Capacitor.isPluginAvailable('NativeHaptics') ? NativeHaptics : null;
}

/** Vibration-API durations (ms) for Android / non-iOS browsers. */
const WEB_IMPACT_MS: Record<HapticImpactStyle, number> = {
    light: 10,
    soft: 12,
    medium: 18,
    rigid: 22,
    heavy: 28,
};

const WEB_NOTIFICATION_MS: Record<HapticNotificationType, number[]> = {
    success: [12, 40, 18],
    warning: [18, 50, 18],
    error: [24, 50, 24, 50, 24],
};

/** Elements that should produce a tap tick under the global tap-haptics listener. */
const INTERACTIVE_SELECTOR =
    'button, a[href], [role="button"], [role="tab"], [role="switch"], [role="menuitem"], ' +
    'input[type="button"], input[type="submit"], input[type="reset"], ' +
    'input[type="checkbox"], input[type="radio"], select, summary';

/**
 * One impact per tap.
 *
 * The global listener ticks on POINTERDOWN for a mouse and POINTERUP for a
 * finger (see installGlobalTapHaptics); a component's own handler fires on
 * CLICK, after both. Both describe the same tap, so the second
 * is noise — felt as a double tap on the bottom nav and the quiz Continue
 * button, and latent on every one of the ~350 call sites that sit on a button.
 *
 * The gate lives in impact() rather than selection() because that is what those
 * call sites actually reach: `hapticFeedback.medium()` never went near the old
 * selection-only throttle. First call in the window wins, which in practice is
 * the global tick — a component's stronger style is dropped rather than the
 * tick delayed, because a tap is a tap whatever the button behind it thinks.
 *
 * NOTIFICATIONS ARE DELIBERATELY NOT GATED. success/warning/error reports the
 * OUTCOME of an action, not the tap that started it, so a right-answer buzz
 * still lands on top of the tick that preceded it. That is two haptics on
 * purpose and it is the one case where two is correct.
 */
const IMPACT_THROTTLE_MS = 200;

/**
 * Per-device on/off preference (plain localStorage, not user-scoped: it's a
 * property of the device's motor, not of the account).
 */
const ENABLED_STORAGE_KEY = 'haptics_enabled';

class HapticService {
    private hapticLabel: HTMLLabelElement | null = null;
    private cachedIosVersion: { major: number; minor: number } | null | undefined = undefined;
    private lastImpactAt = 0;
    private enabled: boolean | undefined = undefined;

    // ---------------------------------------------------------------------
    // Enable / disable
    // ---------------------------------------------------------------------

    /** Whether haptics are switched on for this device. */
    isEnabled(): boolean {
        if (this.enabled === undefined) {
            let stored: string | null = null;
            try {
                stored = localStorage.getItem(ENABLED_STORAGE_KEY);
            } catch {
                /* storage unavailable (private mode / embedded webview) */
            }
            this.enabled = stored === null ? this.defaultEnabled() : stored === '1';
        }
        return this.enabled;
    }

    /** Persist the user's choice; takes effect immediately, app-wide. */
    setEnabled(enabled: boolean): void {
        this.enabled = enabled;
        try {
            localStorage.setItem(ENABLED_STORAGE_KEY, enabled ? '1' : '0');
        } catch {
            /* storage unavailable — the in-memory flag still applies this session */
        }
    }

    /**
     * On by default everywhere the tick is a real haptic, off where it is the
     * vibration motor.
     *
     * Android's two fallbacks — @capacitor/haptics' 50ms open-loop buzz (a
     * binary older than NativeHaptics) and navigator.vibrate in a browser — are
     * the motor, and an app-wide tap tick made of those reads as a rattle, so
     * they stay off until the user asks for them. Where NativeHaptics IS in the
     * binary the tick is the OS's own (View.performHapticFeedback, composed
     * from the handset's tuned primitives), which is as fine as the iPhone's,
     * so that Android defaults on with everyone else.
     *
     * This matters most before login: the Profile → Settings switch that flips
     * it lives behind the auth wall, so whatever this returns is all a user
     * gets through onboarding and the login screen.
     */
    private defaultEnabled(): boolean {
        if (!this.isAndroidDevice()) return true;
        return Capacitor.isPluginAvailable('NativeHaptics');
    }

    private isAndroidDevice(): boolean {
        if (Capacitor.getPlatform() === 'android') return true;
        return typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
    }

    // ---------------------------------------------------------------------
    // Public API
    // ---------------------------------------------------------------------

    /** Impact / selection-style feedback. Defaults to a medium impact. */
    impact(style: HapticImpactStyle = 'medium'): void {
        if (!this.isEnabled()) return;
        // One per tap — see IMPACT_THROTTLE_MS.
        const now = Date.now();
        if (now - this.lastImpactAt < IMPACT_THROTTLE_MS) return;
        this.lastImpactAt = now;
        try {
            switch (this.target()) {
                case 'telegram':
                    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred(style);
                    break;
                case 'native': {
                    const os = osHaptics();
                    const impactStyle = CAPACITOR_IMPACT[style] ?? ImpactStyle.Medium;
                    (os ? os.impact({ style: impactStyle }) : Haptics.impact({ style: impactStyle })).catch((e) =>
                        console.warn('Native haptic impact failed:', e),
                    );
                    break;
                }
                case 'web':
                    this.webHaptic(WEB_IMPACT_MS[style] ?? WEB_IMPACT_MS.medium);
                    break;
            }
        } catch (error) {
            console.warn('Haptic impact failed:', error);
        }
    }

    /** Notification feedback (success / warning / error). */
    notification(type: HapticNotificationType): void {
        if (!this.isEnabled()) return;
        try {
            switch (this.target()) {
                case 'telegram':
                    window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred(type);
                    break;
                case 'native': {
                    const os = osHaptics();
                    const notificationType = CAPACITOR_NOTIFICATION[type] ?? NotificationType.Success;
                    (os
                        ? os.notification({ type: notificationType })
                        : Haptics.notification({ type: notificationType })
                    ).catch((e) => console.warn('Native haptic notification failed:', e));
                    break;
                }
                case 'web':
                    this.webHaptic(WEB_NOTIFICATION_MS[type] ?? WEB_NOTIFICATION_MS.success);
                    break;
            }
        } catch (error) {
            console.warn('Haptic notification failed:', error);
        }
    }

    /**
     * Light selection tick. The collapse window now lives in impact(), so this
     * is exactly a light impact and nothing else — it used to carry its own
     * throttle, which protected only the callers that went through here.
     */
    selection(): void {
        this.impact('light');
    }

    /**
     * App-wide tap feedback: fire a light selection tick whenever an interactive
     * element is pressed, so we don't have to wire haptics into every button by
     * hand. Opt a subtree out with `data-no-haptic`. Returns a cleanup function.
     *
     * A mouse ticks on pointerdown: it cannot start a scroll. A finger ticks on
     * pointerup, and only if the touch stayed a tap. On pointerdown nobody knows
     * yet whether a finger landing on a button is a press or the start of a
     * scroll, so ticking there buzzed every scroll that began on a button. A
     * scroll ends the touch in pointercancel (no tick), and a drag the browser
     * does not take over, such as a swipe on a `touch-action: none` element,
     * ends further than TAP_SLOP_PX from where it started (no tick).
     *
     * Either way the tick lands before the click event, so impact()'s throttle
     * still drops the component's own click-handler haptic.
     */
    installGlobalTapHaptics(): () => void {
        if (typeof document === 'undefined') return () => { };

        const TAP_SLOP_PX = 10;
        let pending: { pointerId: number; x: number; y: number } | null = null;

        const isHapticTarget = (target: EventTarget | null): boolean => {
            const node = target as Element | null;
            if (!node || typeof node.closest !== 'function') return false;
            const el = node.closest(INTERACTIVE_SELECTOR) as HTMLElement | null;
            if (!el) return false;
            // Respect disabled controls and explicit opt-outs.
            if (el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true') return false;
            return !el.closest('[data-no-haptic]');
        };

        const onPointerDown = (event: PointerEvent) => {
            pending = null;
            if (!isHapticTarget(event.target)) return;
            if (event.pointerType === 'mouse') {
                this.selection();
                return;
            }
            pending = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
        };

        const onPointerUp = (event: PointerEvent) => {
            if (!pending || pending.pointerId !== event.pointerId) return;
            const moved = Math.hypot(event.clientX - pending.x, event.clientY - pending.y);
            pending = null;
            if (moved <= TAP_SLOP_PX) this.selection();
        };

        const onPointerCancel = (event: PointerEvent) => {
            if (pending?.pointerId === event.pointerId) pending = null;
        };

        const options: AddEventListenerOptions = { capture: true, passive: true };
        document.addEventListener('pointerdown', onPointerDown, options);
        document.addEventListener('pointerup', onPointerUp, options);
        document.addEventListener('pointercancel', onPointerCancel, options);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown, options);
            document.removeEventListener('pointerup', onPointerUp, options);
            document.removeEventListener('pointercancel', onPointerCancel, options);
        };
    }

    /**
     * Backward-compatible generic entry point used by the old
     * `platformService.hapticFeedback(options)` call shape: a `type` wins over
     * a `style`, otherwise it's an impact defaulting to medium.
     */
    trigger(options: HapticFeedbackOptions = {}): void {
        if (options.type) {
            this.notification(options.type);
        } else {
            this.impact((options.style as HapticImpactStyle) ?? 'medium');
        }
    }

    /** Whether *programmatic* haptics can fire on the current platform. */
    isSupported(): boolean {
        switch (this.target()) {
            case 'telegram':
                return !!window.Telegram?.WebApp?.HapticFeedback;
            case 'native':
                return true;
            case 'web':
                return this.supportsIosWebHaptic() || this.hasVibrationApi();
        }
    }

    /**
     * Whether it's worth offering the user an on/off switch — i.e. a phone or
     * tablet that can actually buzz.
     *
     * `isSupported()` alone isn't enough: desktop Chrome/Firefox define
     * `navigator.vibrate` as a no-op and Telegram Desktop exposes a
     * `HapticFeedback` object, so a Windows/macOS user would see a setting that
     * controls nothing.
     */
    isConfigurable(): boolean {
        return this.isMobileDevice() && this.isSupported();
    }

    /** iPhone/iPad/Android — native app or mobile browser. */
    private isMobileDevice(): boolean {
        if (Capacitor.isNativePlatform()) return true;
        return this.isIosWeb() || this.isAndroidDevice();
    }

    /**
     * Overlay an invisible iOS `<input switch>` on `el` so a *genuine* user tap
     * produces a native haptic. This is the only technique that still works on
     * iOS 26.5+, where programmatic haptics are blocked by Apple. No-op off iOS
     * Safari. Returns a cleanup function to remove the overlay.
     */
    bindElement(el: HTMLElement): () => void {
        if (!this.isEnabled()) return () => {};
        if (!this.isIosWeb()) return () => {};

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.setAttribute('switch', '');
        input.setAttribute('aria-hidden', 'true');
        input.tabIndex = -1;
        input.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;margin:0;opacity:0;cursor:inherit;';

        const previousPosition = el.style.position;
        if (getComputedStyle(el).position === 'static') {
            el.style.position = 'relative';
        }
        el.appendChild(input);

        return () => {
            input.remove();
            el.style.position = previousPosition;
        };
    }

    // ---------------------------------------------------------------------
    // Platform detection
    // ---------------------------------------------------------------------

    private target(): HapticTarget {
        // Telegram first: a Mini App can run inside an iOS/Android browser but
        // should use Telegram's own haptic channel.
        if (window.Telegram?.WebApp?.initData) return 'telegram';
        if (Capacitor.isNativePlatform()) return 'native';
        return 'web';
    }

    // ---------------------------------------------------------------------
    // Web (browser) haptics
    // ---------------------------------------------------------------------

    private webHaptic(pattern: number | number[]): void {
        // iOS Safari has no Vibration API — use the <input switch> trick instead.
        if (this.isIosWeb()) {
            this.triggerIosWebHaptic();
            return;
        }
        if (this.hasVibrationApi()) {
            try {
                navigator.vibrate(pattern);
            } catch {
                /* vibration not allowed in this context — ignore */
            }
        }
    }

    private hasVibrationApi(): boolean {
        return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
    }

    /**
     * iOS version parsed from the UA, or null when this isn't iOS/iPadOS Safari.
     * iPadOS in desktop mode reports as "MacIntel" with touch points, so we
     * account for that too.
     */
    private iosVersion(): { major: number; minor: number } | null {
        if (this.cachedIosVersion !== undefined) return this.cachedIosVersion;
        this.cachedIosVersion = null;

        if (typeof navigator === 'undefined') return this.cachedIosVersion;

        const ua = navigator.userAgent;
        const isIos =
            /iP(hone|ad|od)/.test(ua) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        if (!isIos) return this.cachedIosVersion;

        const match = ua.match(/OS (\d+)_(\d+)/);
        if (match) {
            this.cachedIosVersion = { major: parseInt(match[1], 10), minor: parseInt(match[2], 10) };
        }
        return this.cachedIosVersion;
    }

    private isIosWeb(): boolean {
        return this.iosVersion() !== null;
    }

    /**
     * The programmatic <input switch> haptic works on iOS Safari 17.4–26.4.
     * Apple patched it in 26.5, so we report unsupported there (and bindElement
     * becomes the only path).
     */
    private supportsIosWebHaptic(): boolean {
        const v = this.iosVersion();
        if (!v) return false;
        if (v.major < 17 || (v.major === 17 && v.minor < 4)) return false; // pre-`switch`
        if (v.major > 26 || (v.major === 26 && v.minor >= 5)) return false; // patched in 26.5
        return true;
    }

    private triggerIosWebHaptic(): void {
        if (!this.supportsIosWebHaptic()) return; // 26.5+ — graceful no-op
        this.ensureHapticLabel().click();
    }

    /** Lazily create a single hidden <label><input switch></label> we reuse. */
    private ensureHapticLabel(): HTMLLabelElement {
        if (this.hapticLabel?.isConnected) return this.hapticLabel;

        const label = document.createElement('label');
        label.setAttribute('aria-hidden', 'true');
        label.style.cssText =
            'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;z-index:-2147483647;overflow:hidden;';

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.setAttribute('switch', '');
        input.tabIndex = -1;
        label.appendChild(input);

        document.body.appendChild(label);
        this.hapticLabel = label;
        return label;
    }
}

export const hapticService = new HapticService();

/**
 * Named convenience API — the canonical shape consumed across the app
 * (via `usePlatform().hapticFeedback`). Kept as plain bound closures so it can
 * be passed around as a prop without losing `this`.
 */
export const hapticFeedback = {
    light: () => hapticService.impact('light'),
    medium: () => hapticService.impact('medium'),
    heavy: () => hapticService.impact('heavy'),
    rigid: () => hapticService.impact('rigid'),
    soft: () => hapticService.impact('soft'),
    success: () => hapticService.notification('success'),
    warning: () => hapticService.notification('warning'),
    error: () => hapticService.notification('error'),
    selection: () => hapticService.selection(),
};

export type HapticFeedbackApi = typeof hapticFeedback;
